import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import PageTransitionLoader from './PageTransitionLoader';
import AccessStatusBanner from './AccessStatusBanner';

const Layout = ({ children }) => {
  const { user, role, isAuthenticated, loading } = useAuth();
  const { theme } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLayout, setShowLayout] = useState(false);
  const [isPageTransitioning, setIsPageTransitioning] = useState(false);
  const previousPathRef = useRef('');
  const location = useLocation();
  
  // Check if current path is auth-related (login, signup, etc.)
  // Invite links (/join/…) look the same signed in or out: no sidebar
  const isAuthPage = ['/login', '/signup', '/forgot-password', '/reset-password'].includes(location.pathname) || location.pathname.startsWith('/join/');
  // Every public page (see ROUTES in scripts/prerender.js); a page missing here is drawn inside the dashboard frame
  const PUBLIC_MARKETING_PATHS = ['/', '/faq', '/how-it-works', '/collectors', '/illegal-dumping', '/users', '/accra', '/about', '/blog', '/partners', '/waste-pickup', '/volunteers', '/media', '/privacy', '/terms'];
  // Public pages are linked with a trailing slash (/about/), so compare without it
  const isPublicPage = PUBLIC_MARKETING_PATHS.includes(location.pathname.replace(/(.)\/+$/, '$1')) || location.pathname.startsWith('/blog/') || location.pathname.startsWith('/waste-collection-');
  const isHomePage = isPublicPage;
  
  // Get user's first name from metadata for header greeting
  const firstName = user?.user_metadata?.firstName || user?.user_metadata?.full_name?.split(' ')[0] || 'User';
  
  // Check localStorage as a fallback for authentication state
  const localAuthState = localStorage.getItem('trashdrop_authenticated') === 'true';
  const effectiveAuthState = isAuthenticated || localAuthState;
  
  // Handle page transitions - show loader when navigating between pages
  useEffect(() => {
    if (previousPathRef.current && previousPathRef.current !== location.pathname && !isAuthPage && !isPublicPage) {
      setIsPageTransitioning(true);
      // Hide loader after a short delay to allow lazy component to load
      const timer = setTimeout(() => {
        setIsPageTransitioning(false);
      }, 300);
      return () => clearTimeout(timer);
    }
    previousPathRef.current = location.pathname;
  }, [location.pathname, isAuthPage, isPublicPage]);
  
  useEffect(() => {
    // Only show layout if not on auth page, not homepage, and either authenticated or loading is complete
    setShowLayout(!isAuthPage && !isPublicPage && effectiveAuthState);
  }, [isAuthPage, isPublicPage, effectiveAuthState, loading]);
  
  // If we're on an auth page or homepage, only render children without layout
  if (isAuthPage || isHomePage) {
    return <>{children}</>;
  }
  
  // For authenticated pages, render the full layout with sidebar and navbar
  return (
    <div data-theme={theme} className="flex h-screen overflow-hidden" style={{ background: 'var(--td-page-bg)', color: 'var(--td-text-primary)', transition: 'background 0.25s ease, color 0.25s ease' }}>
      {/* Sidebar for authenticated users - 250px width */}
      {showLayout && (
        <>
          {/* Desktop sidebar - fixed position */}
          <div className="hidden md:block fixed top-0 left-0 w-64 h-full">
            <Sidebar />
          </div>
          
          {/* Mobile sidebar overlay */}
          <div className={`fixed inset-0 z-40 md:hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`}>
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setIsMobileMenuOpen(false)}></div>
            <div className="relative flex-1 flex flex-col max-w-xs w-64" style={{ background: 'var(--td-sidebar-bg)' }}>
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="sr-only">Close sidebar</span>
                  <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                <Sidebar />
              </div>
            </div>
          </div>
        </>
      )}
      
      {/* Main content column; in the dashboard a light surface, so focus rings are dark green (tokens.css) */}
      <div data-surface={showLayout ? 'light' : undefined} className={`flex-1 flex flex-col ${showLayout ? 'md:ml-64' : ''} h-screen overflow-hidden`}>
        {/* Fixed top navbar — rendered in normal flow so it takes up space in the flex column */}
        {showLayout && (
          <div className="flex-shrink-0 h-14 w-full" style={{ zIndex: 20, position: 'relative' }}>
            <Navbar 
              toggleMobileMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
              isMobileMenuOpen={isMobileMenuOpen}
            />
          </div>
        )}
        
        {/* Scrollable page content — sits directly below the navbar, never behind it */}
        <main className="flex-1 overflow-y-auto px-6 pt-5 pb-5 relative" style={{ background: 'var(--td-page-bg)' }}>
          {/* Page transition loader overlay */}
          {isPageTransitioning && <PageTransitionLoader />}
          
          {loading ? (
            <div className="flex items-center justify-center h-full" style={{ background: 'var(--td-page-bg)' }}>
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-td-forest"></div>
            </div>
          ) : (
            <>
              {showLayout && <AccessStatusBanner />}
              {children}
            </>
          )}
        </main>
        
        {/* Footer */}
        {showLayout && (
          <footer className="flex-shrink-0 py-3 px-4 sm:px-6 lg:px-8" style={{ background: 'var(--td-footer-bg)', borderTop: '1px solid var(--td-footer-border)' }}>
            <div className="flex flex-col md:flex-row justify-between items-center text-sm" style={{ color: 'var(--td-footer-text)' }}>
              <p> {new Date().getFullYear()} TrashDrop. All rights reserved.</p>
              <div className="flex space-x-4 mt-2 md:mt-0">
                <a href="/privacy/" style={{ color: 'var(--td-footer-text)' }} className="hover:opacity-80">Privacy Policy</a>
                <a href="/terms/" style={{ color: 'var(--td-footer-text)' }} className="hover:opacity-80">Terms of Service</a>
                <a href="mailto:hello@trashdrops.com" style={{ color: 'var(--td-footer-text)' }} className="hover:opacity-80">Contact Support</a>
              </div>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
};

export default Layout;
